import { useTranslation } from "react-i18next";
import { Box } from "lucide-react";
import type { ManualPlatforms } from "../PlatformSelector.js";
import { resolveUnityLaunch } from "../webgl-panel/unity-launch.js";
import { readBootstrap } from "../../../tenant/bootstrap.js";
import { Button } from "@/components/ui/button";

/**
 * What the reader shows for a manual without a 2D view (ADR-057, legacy `CanStartReadMode`). With 3D it offers the 3D view; a new tab
 * can't open without a click, so unlike the legacy it asks for one. Otherwise it says where the manual runs.
 */
export function ManualPlatformGate({
  platforms,
  manualName,
  realm,
  facilityId,
  manualId,
}: {
  platforms: ManualPlatforms;
  manualName: string;
  realm: string | undefined;
  facilityId: number | null;
  manualId: number;
}) {
  const { t, i18n } = useTranslation();

  if (platforms.is3dWebManual) {
    const launch = resolveUnityLaunch({
      realm,
      facilityId,
      manualId,
      locale: i18n.language,
      editorUrl: readBootstrap()?.unityEditorUrl,
      readOnly: false,
      mode: "view",
    });
    const url = launch.url;
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <h2 className="m-0 text-lg font-semibold">{manualName}</h2>
        {url ? (
          <>
            <p className="m-0 text-sm">{t("manualManagement.platforms.only3d")}</p>
            <Button variant="accent" className="h-[38px] rounded-[10px] px-3 text-[13px]" onPress={() => window.open(url, "_blank", "noopener,noreferrer")}>
              <Box aria-hidden="true" />
              {t("manualManagement.platforms.open3d")}
            </Button>
          </>
        ) : (
          <p className="m-0 text-sm">{t("manualManagement.platforms.no3dHere")}</p>
        )}
      </div>
    );
  }

  const names: string[] = [];
  const devices: string[] = [];
  if (platforms.isVrManual) {
    names.push(t("manualManagement.platforms.platformVr"));
    devices.push(t("manualManagement.platforms.deviceVr"));
  }
  if (platforms.isArManual) {
    names.push(t("manualManagement.platforms.platformAr"));
    devices.push(t("manualManagement.platforms.deviceTablet"));
  }
  return (
    <div className="flex flex-col gap-3 p-6">
      <h2 className="m-0 text-lg font-semibold">{manualName}</h2>
      <p className="m-0 text-sm">
        {names.length > 0
          ? t("manualManagement.platforms.onlyOn", { platforms: names.join(` ${t("manualManagement.platforms.and")} `), devices: devices.join(", ") })
          : t("manualManagement.platforms.nowhere")}
      </p>
    </div>
  );
}
